import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useReportCurrency } from "@/profitability/currency";
import type { GoalCheckSummary } from "@/profitability/types";
import { describe } from "./format";
import { GoalCoverage } from "./goal-coverage";
import { MatchedFigures } from "./matched-figures";

const SHOWN = 5;

export const GoalChecks = ({
  checks,
  subject = "the goal",
}: {
  checks: GoalCheckSummary;
  /** What the figures were checked against, as the sentence names it. */
  subject?: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const currency = useReportCurrency();
  const failed = checks.failed;
  const matched = checks.matched ?? [];
  if (!failed.length)
    return (
      <Stack gap={1} align="stretch" className="w-full">
        <Stack direction="row" gap={1} align="center" data-testid="goal-checks">
          <CheckCircleIcon className="size-4 shrink-0 text-success" />
          <Text>
            All {checks.total} figures checked against {subject} match.
          </Text>
        </Stack>
        <GoalCoverage coverage={checks.coverage} />
        <MatchedFigures matched={matched} />
      </Stack>
    );

  const more = failed.length - SHOWN;
  return (
    <Stack gap={1} align="stretch" className="w-full" data-testid="goal-checks">
      <Stack direction="row" gap={1} align="center">
        <XCircleIcon className="size-4 shrink-0 text-danger" />
        <Text tone="destructive">
          {failed.length} of {checks.total} figures checked against {subject}{" "}
          differ.
        </Text>
      </Stack>
      <ul className="list-disc pl-5">
        {(expanded ? failed : failed.slice(0, SHOWN)).map((check) => (
          <li key={check.label} className="[overflow-wrap:anywhere]">
            <Text tone="muted">{describe(check, currency)}</Text>
          </li>
        ))}
      </ul>
      {more > 0 && (
        <Stack direction="row">
          <Button
            size="sm"
            variant="ghost"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? "Show fewer" : `Show ${more} more`}
          </Button>
        </Stack>
      )}
      <GoalCoverage coverage={checks.coverage} />
      <MatchedFigures matched={matched} />
    </Stack>
  );
};
